import React, { useCallback, useEffect, useState } from 'react';
import { ArrowRight, Clock3, Hash, PhoneCall, PhoneForwarded, PhoneIncoming, PhoneOutgoing, Timer, User, UserRound } from 'lucide-react';
import { callsAPI } from '../../../../api/client';
import { prettyNumber } from '../../CallDialog';
import { cause, CALL_STATUS, formatDuration, LIVE_STATUSES, timeline, when } from '../callModel';
import AudioPlayer from '../../recordings/AudioPlayer';
import { CallStatus } from './CallBadges';

const TABS = ['Overview', 'Timeline', 'Recording', 'History'];

function Row({ icon: Icon, label, children }) {
  return <div className="cl-detail-row"><Icon size={14} /><span>{label}</span><b>{children}</b></div>;
}

/**
 * Call Details (right of the table, like the Super Admin's Subscriber Control): the call with Overview, Timeline,
 * Recording and History (other calls with this number), and Call back / full details.
 */
export default function CallDetailsPanel({ callId, canCall, onCall, onOpen, onSelect }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('Overview');
  const load = useCallback(() => callsAPI.get(callId).then((res) => setData(res.data)).catch(() => setData(false)), [callId]);
  useEffect(() => { setData(null); load(); }, [load]);
  const live = data && LIVE_STATUSES.includes(data.call.status);
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [live, load]);

  if (data === false) return <aside className="panel cl-details"><p className="cell-muted">This call couldn't be loaded.</p></aside>;
  if (!data) return <aside className="panel cl-details"><p className="cell-muted">Loading…</p></aside>;
  const { call, history } = data;
  const inbound = call.direction === 'inbound';
  const who = call.contact_name || prettyNumber(call.other_number);

  return (
    <aside className="panel cl-details" aria-label="Call details">
      <div className="cl-details-head"><b>Call Details</b>
        <button type="button" className="link-button" onClick={() => onOpen(call)}>View Full Details <ArrowRight size={12} /></button></div>
      <div className="cl-identity">
        <span className={`cl-avatar large ${inbound ? 'green' : 'blue'}`}>{inbound ? <PhoneIncoming size={22} /> : <PhoneOutgoing size={22} />}</span>
        <div><div className="cl-identity-name"><b>{who}</b><CallStatus status={call.status} /></div>
          <small>{inbound ? 'Incoming' : 'Outgoing'} call · {when(call.started_at)}</small></div>
      </div>
      <div className="cl-tabs" role="tablist">
        {TABS.map((t) => <button type="button" role="tab" key={t} aria-selected={tab === t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>{t}</button>)}
      </div>

      {tab === 'Overview' && (
        <div>
          <Row icon={UserRound} label="Customer">{call.contact_name || 'Not in contacts'}</Row>
          <Row icon={PhoneCall} label="Customer number">{prettyNumber(call.other_number)}</Row>
          <Row icon={PhoneForwarded} label="Company number">{prettyNumber(call.company_number)}</Row>
          <Row icon={User} label="Team member">{call.user_name || '—'}</Row>
          <Row icon={Timer} label="Duration">{formatDuration(call.duration_seconds)}{call.answered_at ? ' talk time' : ''}</Row>
          <Row icon={Clock3} label="Outcome">{CALL_STATUS[call.status]?.[0] || call.status}{cause(call.hangup_cause) ? ` · ${cause(call.hangup_cause)}` : ''}</Row>
          <Row icon={Hash} label="Call ID">#{call.id}</Row>
        </div>
      )}
      {tab === 'Timeline' && (
        <ol className="call-timeline">
          {timeline(call).map(([text, at, color], i) => <li key={i} style={{ '--dot': color }}><b>{text}</b>{at}</li>)}
        </ol>
      )}
      {tab === 'Recording' && (
        call.recording_id
          ? <div className="cl-recording"><small>{call.status === 'voicemail' ? 'Voicemail' : 'Call recording'}</small>
              <AudioPlayer key={call.recording_id} recordingId={call.recording_id} duration={call.duration_seconds} label={call.status === 'voicemail' ? 'Voicemail' : 'Call recording'} /></div>
          : <p className="cell-muted">No recording for this call{call.status === 'completed' ? ' (call recording may be off in Settings)' : ''}.</p>
      )}
      {tab === 'History' && (
        <div className="cl-history">
          {history.length === 0 && <p className="cell-muted">This is the only call with this number so far.</p>}
          {history.map((c) => (
            <button type="button" key={c.id} onClick={() => onSelect(c.id)}>
              <span><b>{when(c.started_at)}</b><small>{c.direction === 'inbound' ? 'Incoming' : 'Outgoing'} · {c.user_name || '—'} · {formatDuration(c.duration_seconds)}</small></span>
              <CallStatus status={c.status} />
            </button>
          ))}
        </div>
      )}

      <div className="cl-actions">
        {canCall && <button type="button" className="primary-action" onClick={() => onCall(call)}><PhoneCall size={14} />Call back</button>}
        <button type="button" className="secondary-action" onClick={() => onOpen(call)}>Full call page <ArrowRight size={14} /></button>
      </div>
    </aside>
  );
}
